export interface State<TValues> {
  values: TValues;
}

export interface Action<TValues> {
  index?: number;
  input?: keyof TValues | ((currentValues: State<TValues>["values"]) => State<TValues>["values"]);
  type?: "RESET";
  value?: TValues[keyof TValues];
}

export function createReducer<TValues>(initialState: State<TValues>) {
  return function reducer(state: State<TValues>, action: Action<TValues>): State<TValues> {
    switch (action.type) {
      case "RESET":
        return {
          ...initialState,
        };
      default:
        if (typeof action.input === "function") {
          return { values: action.input(state.values) };
        } else if (typeof action.input === "string") {
          if (action.index === undefined) {
            const updatedValues = {
              ...state.values,
              [action.input]: action.value,
            };
            return { values: updatedValues };
          }

          const updatedValues = {
            ...state.values,
            [action.input]: Object.assign([], state.values[action.input], {
              [action.index]: action.value,
            }),
          };
          return { values: updatedValues };
        } else {
          return state;
        }
    }
  };
}

export type FormState<TValues> = {
  formIsEdited: boolean;
  formIsValid: boolean;
  inputValidities: Record<string, boolean>;
  inputValues: TValues;
  lastInput?: string;
};

export type FormAction<TValues> = {
  input?: string;
  isValid?: boolean;
  type?: "TOTAL_RESET" | "RESET" | "SAVE";
  value?: TValues[keyof TValues];
};

export function createFormReducer<TValues>(initialState: FormState<TValues>) {
  return function reducer(
    state: FormState<TValues>,
    action: FormAction<TValues>
  ): FormState<TValues> {
    switch (action.type) {
      case "RESET":
        return {
          ...initialState,
          formIsEdited: state.formIsEdited,
          lastInput: state.lastInput,
        };
      case "TOTAL_RESET":
        return {
          ...initialState,
          formIsEdited: false,
          lastInput: undefined,
        };
      case "SAVE":
        return {
          ...initialState,
          formIsEdited: false,
          lastInput: undefined,
        };
      default:
        if (action.input) {
          const updatedValues = {
            ...state.inputValues,
            [action.input]: action.value,
          };
          const updatedValidities = {
            ...state.inputValidities,
            [action.input]: Boolean(action.isValid),
          };
          let updatedFormIsValid = true;
          for (const key in updatedValidities) {
            updatedFormIsValid = updatedFormIsValid && updatedValidities[key];
            if (!updatedFormIsValid) break;
          }
          return {
            formIsEdited: true,
            formIsValid: updatedFormIsValid,
            inputValidities: updatedValidities,
            inputValues: updatedValues,
            lastInput: action.input,
          };
        } else {
          return state;
        }
    }
  };
}

type InputState = {
  focussed: boolean;
  hasBeenBlurred: boolean;
  isValid: boolean;
  touched: boolean;
  value: string;
};

type InputAction =
  | { isValid: boolean; type: "INPUT_CHANGE"; value: string }
  | { isValid: boolean; type: "INPUT_CHANGE_NO_FOCUS"; value: string }
  | { type: "INPUT_FOCUS" }
  | { type: "INPUT_BLUR" };

export const createInputReducer =
  () =>
  (state: InputState, action: InputAction): InputState => {
    switch (action.type) {
      case "INPUT_CHANGE":
        return {
          ...state,
          focussed: true,
          isValid: action.isValid,
          touched: true,
          value: action.value,
        };
      case "INPUT_CHANGE_NO_FOCUS":
        return {
          ...state,
          isValid: action.isValid,
          value: action.value,
        };
      case "INPUT_FOCUS":
        return {
          ...state,
          focussed: true,
          touched: true,
        };
      case "INPUT_BLUR":
        return {
          ...state,
          focussed: false,
          hasBeenBlurred: true,
          touched: true,
        };
      default:
        return state;
    }
  };
